import React from 'react';
import { TextField, Typography, styled } from '@mui/material';
import { Control, Controller } from 'react-hook-form';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import { FormValues } from '../../types';

const InputContainer = styled(Column)({
  width: '100%',
  maxWidth: '400px',
});

type Props = {
  control: Control<FormValues>;
  hasLinkedPortal: boolean;
  sharedFieldWarningText: string;
  sharedFieldDotColor: string;
};

const PortalGeneral: React.FC<Props> = ({
  control,
  hasLinkedPortal,
  sharedFieldWarningText,
  sharedFieldDotColor,
}) => {
  return (
    <Column spacing="lg">
      {hasLinkedPortal ? (
        <Typography variant="body2" color="grey.500">
          {sharedFieldWarningText}
        </Typography>
      ) : null}
      <InputContainer>
        <FormField
          label="Verified reply-to email address"
          tooltip="This is your firm's email address, from which system emails are sent. Please ensure this email has been verified by Further's team before adding it here."
          dotColor={hasLinkedPortal ? sharedFieldDotColor : undefined}
          dotTooltipText={
            hasLinkedPortal
              ? 'This field is shared between your EIS and LP systems. Changes here will affect both systems.'
              : undefined
          }
        >
          <Controller
            name="verifiedReplyToEmailAddress"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Enter email address"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FormField>
      </InputContainer>

      <InputContainer spacing="lg">
        <FormField
          label="Firm contact email"
          tooltip="Enter the email address investors can use to contact you. This is displayed in various places in the portal and in email and other communications."
        >
          <Controller
            name="contactEmail"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Enter email address"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FormField>

        <FormField
          label="Investor service phone number"
          tooltip="Enter the phone number investors can use to contact you. This is displayed in various places in the portal."
        >
          <Controller
            name="contactPhoneNumber"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Enter phone number"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FormField>

        <FormField
          label="Phone contact days and times"
          tooltip='This is when investors can call you, e.g. "Mon-Fri 9:00-17:00". This is displayed in various places in the portal.'
        >
          <Controller
            name="contactHours"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="When investors can call you, e.g. Mon-Fri 9:00-17:00"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </FormField>
      </InputContainer>
    </Column>
  );
};

export default PortalGeneral;
